import {memo, useContext} from 'react'

import PropTypes from 'prop-types'

import Context from '@s-ui/react-context'
import ActionAdFavorite, {actionAdFavoriteTrackingOrigins, actionAdFavoriteTypes} from '@adv-mt/mt-actions-ad-favorite'
import {Badge, BADGE_DESIGNS, BADGE_INTENTS, BADGE_SIZES} from '@adv-mt/ui/badge'
import {LOADING, MediaImage, OBJECT_FIT} from '@adv-mt/ui/media-image'
import {Panel, PANEL_COLORS, PANEL_ELEVATION, PANEL_ROUNDED} from '@adv-mt/ui/panel'
import {Body2, Caption, Headline2} from '@adv-mt/ui/typography'

import useCardRelatedAdClick from '../use-card-related-ad-click.js'
import CardRelatedAdV2PriceDrop from './price-drop.js'

const BASE_CLASS = 'mt-CardRelatedAdV2'
const DEFAULT_PHOTO = 'images/vehicle-no-photo.png'
const MEDIA_ASPECT_RATIO = '4 / 3'

const isModifiedClick = event => event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey

const CardRelatedAdV2 = ({
  ad,
  extendedTrackingData = {},
  hasFavouriteAction = false,
  listType = '',
  onClickCard = () => {},
  pageNameOrigin = '',
  pageType = '',
  position = 0
}) => {
  const {domain, i18n} = useContext(Context)

  const handleClick = useCardRelatedAdClick({
    ad,
    extendedTrackingData,
    listType,
    onClickCard,
    pageNameOrigin,
    pageType,
    position
  })

  const config = domain.get('config')
  const {OFFER_TYPES} = config.get('vehicleSpecifications')
  const defaultPhotoSrc = `${config.get('urlStatics')}${DEFAULT_PHOTO}`

  const {imgUrl, isFavorite, km, monthlySubscriptionFee, offerType, price, priceDrop, title, url, year} = ad

  const offerTypeLabel = offerType?.id != null && OFFER_TYPES[offerType.id] && i18n.t(OFFER_TYPES[offerType.id].name)
  const hasKm = Boolean(km)
  const hasPrice = Boolean(price)
  const hasYear = Boolean(year)
  const hasPriceDrop = Boolean(priceDrop) && !monthlySubscriptionFee

  // Modified clicks (new tab, new window, download) are left to the browser; they are only tracked.
  const handleLinkClick = event => {
    if (isModifiedClick(event)) {
      handleClick({shouldNavigate: false})
      return
    }

    event.preventDefault()
    handleClick()
  }

  return (
    <Panel
      as="article"
      className={BASE_CLASS}
      color={PANEL_COLORS.BASE}
      elevation={PANEL_ELEVATION.M}
      rounded={PANEL_ROUNDED.L}
    >
      <div className={`${BASE_CLASS}-media`}>
        <MediaImage
          alt=""
          aspectRatio={MEDIA_ASPECT_RATIO}
          fallbackSrc={defaultPhotoSrc}
          loading={LOADING.LAZY}
          objectFit={OBJECT_FIT.COVER}
          src={imgUrl || defaultPhotoSrc}
        />
      </div>
      <div className={`${BASE_CLASS}-info`}>
        {offerTypeLabel && (
          <Badge design={BADGE_DESIGNS.SOFT} intent={BADGE_INTENTS.NEUTRAL} size={BADGE_SIZES.SMALL}>
            {offerTypeLabel}
          </Badge>
        )}
        <div className={`${BASE_CLASS}-main`}>
          {hasPriceDrop && <CardRelatedAdV2PriceDrop priceDropData={priceDrop} />}
          {hasPrice && (
            <Headline2 as="p" className={`${BASE_CLASS}-price`}>
              {i18n.c(price)}
            </Headline2>
          )}
          {monthlySubscriptionFee ? (
            <>
              <Caption as="span" className={`${BASE_CLASS}-priceLabel`}>
                {i18n.t('COMMON_FROM')}
              </Caption>
              <Headline2 as="p" className={`${BASE_CLASS}-price`}>
                {i18n.c(monthlySubscriptionFee)}
                <span className={`${BASE_CLASS}-priceSub`}>{`/${i18n
                  .t('COMMON.PLURALS.MONTH', 1)
                  .toLowerCase()}`}</span>
              </Headline2>
            </>
          ) : null}
          <Body2 as="p" className={`${BASE_CLASS}-title`}>
            <a className={`${BASE_CLASS}-link`} href={url} onClick={handleLinkClick}>
              {title}
            </a>
          </Body2>
          {(hasKm || hasYear) && (
            <p className={`${BASE_CLASS}-details`}>
              {hasKm && <Caption as="span">{`${i18n.n(km)} km`}</Caption>}
              {hasKm && hasYear && <span aria-hidden className={`${BASE_CLASS}-detailsDivider`} />}
              {hasYear && <Caption as="span">{year}</Caption>}
            </p>
          )}
        </div>
      </div>
      {/* After the link in the DOM so the card itself is the first tab stop; absolutely positioned over the media. */}
      {hasFavouriteAction && (
        <ActionAdFavorite
          ad={ad}
          className={`${BASE_CLASS}-favorite`}
          extendedTrackingData={extendedTrackingData}
          isFavorite={isFavorite}
          trackingOrigin={actionAdFavoriteTrackingOrigins.RELATED_AD_FAVORITED}
          type={actionAdFavoriteTypes.OVERLAY_ICON_BUTTON}
        />
      )}
    </Panel>
  )
}

CardRelatedAdV2.displayName = 'CardRelatedAdV2'

CardRelatedAdV2.propTypes = {
  ad: PropTypes.object.isRequired,
  extendedTrackingData: PropTypes.object,
  hasFavouriteAction: PropTypes.bool,
  listType: PropTypes.string,
  onClickCard: PropTypes.func,
  pageNameOrigin: PropTypes.string,
  pageType: PropTypes.string,
  position: PropTypes.number
}

const MemoCardRelatedAdV2 = memo(CardRelatedAdV2)
MemoCardRelatedAdV2.displayName = 'MemoCardRelatedAdV2'

export default MemoCardRelatedAdV2
